import { useEffect, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import { todayLocalDate } from '@/lib/intl';

/**
 * The clinical-study module's contribution to the navigation counter: how many Visits are
 * classified ELAPSING or ELAPSED as of today, across the Studies this caller may read.
 *
 * WHY THE COUNTER NEEDED A NEW BACKEND READ. The module's two at-risk surfaces are both per-Study,
 * and navigation has no Study in scope. Fanning out from the browser — list the Studies, then one
 * at-risk request per Study — would issue N+1 requests on every page load to render a badge, so
 * the aggregate is computed server-side by `GET /v1/clinical-study/me/attention`, which sums the
 * SAME `listAtRiskVisits` read the at-risk screens render. The badge and the screen it points at
 * therefore cannot disagree.
 *
 * EVERY FAILURE IS SILENT, AND THAT IS THE POINT. Loading, a network error, a 403 from a caller
 * holding no clinical-study role, and an explicit `null` (a study administrator, who may not see
 * the at-risk surface at all) all return `undefined` — no badge. A counter is an ambient hint in
 * the corner of a navigation rail: it must never produce an error state, a spinner, or a zero
 * where the truth is "not known". A wrong number in navigation is read as fact, which is the same
 * reason this counter did not ship at all until a real read existed to produce it.
 *
 * `asOfDate` comes from `todayLocalDate()`, the LOCAL calendar day — never
 * `toISOString().slice(0, 10)`, which is the UTC day and is off by one near midnight in CET. The
 * at-risk screens already read it from that same helper, so badge and screen agree on which day it
 * is.
 */
export function useClinicalStudyAttention(organizationId: string | undefined): number | undefined {
  const [count, setCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    // No organization means no `X-Active-Organization-Id` for the ambient middleware to attach,
    // and the route would 400. Nothing is requested, and any count from a previous organization is
    // dropped rather than left on screen attached to the wrong one.
    if (organizationId === undefined) {
      setCount(undefined);
      return;
    }

    let active = true;
    // Cleared before the request rather than after it: while the answer for the NEW organization
    // is in flight, the honest badge is no badge, not the previous organization's number.
    setCount(undefined);

    apiClient
      .GET('/v1/clinical-study/me/attention', {
        params: { query: { asOfDate: todayLocalDate() } },
      })
      .then(({ data, error, response }) => {
        if (!active) {
          return;
        }
        if (error !== undefined || !response.ok || data === undefined) {
          return;
        }
        setCount(data.atRiskVisitCount ?? undefined);
      })
      .catch(() => {
        // Deliberately empty: see the silent-failure note above.
      });

    return () => {
      active = false;
    };
  }, [organizationId]);

  return count;
}
